import React from 'react'
import { View, StyleSheet, Pressable } from 'react-native'
import { CommonActions, useNavigation } from '@react-navigation/native'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
// Deep-import to avoid bundling the entire free-solid-svg-icons package (see HomeScreen.tsx).
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight'
import { SailingsData } from '../models/Sailing'
import { formatItinerary, formatSailDate, getUpcomingSailings } from '../utils/helpers'
import { Typography } from './Typography'
import { Card } from './Card'
import { DCL_BLUE } from './Header'
import sailingsData from '../data/sailings.json'

const { sailings } = sailingsData as SailingsData

const styles = StyleSheet.create({
  chipList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: DCL_BLUE,
  },
  chipSelected: {
    backgroundColor: DCL_BLUE,
  },
  chipPressed: {
    opacity: 0.7,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#EEEEEE',
  },
  firstRow: {
    borderTopWidth: 0,
  },
  rowPressed: {
    backgroundColor: '#F5F7FC',
  },
  rowText: {
    flex: 1,
    gap: 2,
  },
  itinerary: {
    fontSize: 12,
    lineHeight: 16,
  },
})

const ShipChip: React.FC<{ shipName: string; selected: boolean; onToggle: () => void }> = ({
  shipName,
  selected,
  onToggle,
}) => (
  <Pressable
    style={({ pressed }) => [styles.chip, selected && styles.chipSelected, pressed && styles.chipPressed]}
    accessibilityRole="button"
    accessibilityState={{ selected }}
    onPress={onToggle}>
    <Typography variant="body" color={selected ? '#FFFFFF' : DCL_BLUE}>{shipName.replace(/^Disney\s+/, '')}</Typography>
  </Pressable>
)

const SailingRow: React.FC<{ label: string; itinerary: string; sailingId: string; isFirst: boolean }> = ({
  label,
  itinerary,
  sailingId,
  isFirst,
}) => {
  const navigation = useNavigation()

  return (
    <Pressable
      style={({ pressed }) => [styles.row, isFirst && styles.firstRow, pressed && styles.rowPressed]}
      accessibilityRole="link"
      accessibilityLabel={`View availability for ${label}. ${itinerary}`}
      onPress={() => {
        navigation.dispatch(
          CommonActions.navigate({
            name: 'Sailing',
            params: { sailing: sailingId },
          })
        )
      }}>
      <View style={styles.rowText}>
        <Typography variant="body" color={DCL_BLUE}>{label}</Typography>
        {itinerary.length > 0 && (
          <Typography variant="body" color="#6E6E6E" style={styles.itinerary}>{itinerary}</Typography>
        )}
      </View>
      <FontAwesomeIcon icon={faChevronRight} size={14} color={DCL_BLUE} />
    </Pressable>
  )
}

export const UpcomingSailings: React.FC = () => {
  const [selectedShips, setSelectedShips] = React.useState<string[]>([])

  const upcoming = React.useMemo(() => getUpcomingSailings(sailings), [])
  const shipNames = React.useMemo(() => [...new Set(upcoming.map((sailing) => sailing.shipName))].sort(), [upcoming])

  // No chips selected means no filter, which keeps the default view showing everything.
  const visible = React.useMemo(
    () => (selectedShips.length === 0 ? upcoming : upcoming.filter((sailing) => selectedShips.includes(sailing.shipName))),
    [upcoming, selectedShips]
  )

  const toggleShip = (shipName: string) => {
    setSelectedShips((current) =>
      current.includes(shipName) ? current.filter((name) => name !== shipName) : [...current, shipName]
    )
  }

  if (upcoming.length === 0) {
    return null
  }

  return (
    <View>
      <Typography variant="title" style={{ marginBottom: 16 }}>Upcoming sailings</Typography>
      <View style={styles.chipList}>
        {shipNames.map((shipName) => (
          <ShipChip
            key={shipName}
            shipName={shipName}
            selected={selectedShips.includes(shipName)}
            onToggle={() => toggleShip(shipName)}
          />
        ))}
      </View>
      <Card>
        {visible.map((sailing, index) => (
          <SailingRow
            key={sailing.sailingId}
            sailingId={sailing.sailingId}
            isFirst={index === 0}
            label={`${sailing.shipName} - ${formatSailDate(sailing.sailDate)} - ${sailing.nights} ${sailing.nights === 1 ? 'night' : 'nights'}`}
            itinerary={formatItinerary(sailing)}
          />
        ))}
      </Card>
    </View>
  )
}
